import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useOutletContext } from "react-router-dom";
import { catalogByPart, getReports, saveReportFilters } from "../../../apis";
import { useTranslation } from "react-i18next";
import { Paginator } from "./Paginator/Paginator";
import { Box, Fade, Typography, useTheme } from "@mui/material";
import { TableSkeleton } from "../../Loading/Table/Table";
import queryString from "query-string";
import { ReportTable } from "./ReportTable/ReportTable";
import { EGPageHeader } from "../../Title/PageHeader/EGPageHeader";
import { SearchBar } from "./Filter/SearchBar/SearchBar";
import { useSnackbar } from "notistack";
import { Filters } from "./Filter/Filters";
import dayjs from "dayjs";

// Persistencia de la preferencia "reportes por página"
const REPORTS_PER_PAGE_KEY = "reportsPerPage";
const VALID_LIMITS = [10, 15, 20]; // Debe coincidir con las opciones del Paginator
const DEFAULT_LIMIT = 10;
// Lee la preferencia guardada; cae al default si no existe o es inválida
const getStoredLimit = () => {
  const stored = Number(localStorage.getItem(REPORTS_PER_PAGE_KEY));
  return VALID_LIMITS.includes(stored) ? stored : DEFAULT_LIMIT;
};

// All reports authorized to view
export const ReportsList = () => {
  // ----- Custom Hooks ----- //
  const [t] = useTranslation("report");
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const { reloadSavedFilters } = useOutletContext();
  //----> Initial States <----//
  const [isLoading, setIsLoading] = useState(true);
  // Paginator
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });
  const [limit, setLimit] = useState(getStoredLimit); // Limit of results per consult (preferencia persistida)
  const [offset, setOffset] = useState(0); // Set offset to getData
  const [currentPage, setCurrentPage] = useState(0); // Current Page
  const pageNumLimit = 10; // Limit of pages on screen (fijo)
  const [maxPageNum, setMaxPageNum] = useState(10); // Number of page max on screen
  const [minPageNum, setMinPageNum] = useState(0); // Number of page min on screen
  const [searchPag, setSearchPag] = useState(""); // Value of input Search
  // Reports data
  const [listReports, setListReports] = useState([]);
  const [expiredReports, setExpiredReports] = useState(null);
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  // Filters general values
  const [filterValues, setFilterValues] = useState({});
  const [localFilterValues, setLocalFilterValues] = useState({});
  // Advanced Search
  const [catalogSearch, setCatalogSearch] = useState([]);
  const [localCatalogSearch, setLocalCatalogSearch] = useState([]);
  // Color Tags from user catalog
  const [tags, setTags] = useState([]);
  // Input Search
  const [inputSearchType, setInputSearchType] = useState("folio"); // Engine search
  const [inputSearchValue, setInputSearchValue] = useState(""); // Input search value
  const [filterName, setFilterName] = useState(""); // Input filter name

  // onSearch report
  const hdlSearchReport = useCallback(
    async ({
      updateOffset = offset,
      filters = filterValues,
      catalog = catalogSearch,
      skipURLUpdate = false,
      searchValue = inputSearchValue,
      clearInput = false,
      updateLimit = limit,
    }) => {
      setIsLoading(true);
      setLimit(updateLimit);

      const trimmedSearch = (searchValue ?? "").trim();
      const params = {
        ...filters,
        ...(catalog.length > 0 && { catalog: catalog.join(";") }),
        ...(trimmedSearch && { [inputSearchType]: trimmedSearch }),
        ...(updateLimit === DEFAULT_LIMIT ? {} : { limit: updateLimit }),
        ...(updateOffset === 0 ? {} : { offset: updateOffset }),
      };

      const query = queryString.stringify(params, {
        skipEmptyString: true,
        skipNull: true,
      });
      const currentParams = queryString.parse(location.search);
      const nextParams = queryString.parse(query);
      // Clean input search and apply
      const clean = (obj) =>
        Object.fromEntries(
          Object.entries(obj).filter(([_, v]) => v !== "" && v != null)
        );

      const areEqual =
        JSON.stringify(clean(currentParams)) ===
        JSON.stringify(clean(nextParams));

      // Si vamos a actualizar la URL, dejamos que el useEffect [location.search]
      // dispare el fetch para evitar duplicados (navigate + fetch local en el mismo handler).
      let didNavigate = false;
      if (!skipURLUpdate) {
        const cleanNext = clean(nextParams);
        const hasUsefulParams = Object.keys(cleanNext).length > 0;

        if (hasUsefulParams && !areEqual) {
          navigate(`?${query}`, { replace: true });
          didNavigate = true;
        } else if (clearInput) {
          const cleanedParams = { ...cleanNext };
          delete cleanedParams[inputSearchType];
          const newQuery = queryString.stringify(cleanedParams, {
            skipEmptyString: true,
            skipNull: true,
          });
          navigate(`?${newQuery}`, { replace: true });
          didNavigate = true;
        }
      }

      if (didNavigate) return;

      try {
        const data = await getReports(params);
        setPagination({
          count: data?.count || 0,
          next: data?.next || null,
          previous: data?.previous || null,
        });
        setExpiredReports(data.results.expired_report);
        setListReports(data.results.reports || []);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      filterValues,
      offset,
      limit,
      inputSearchValue,
      inputSearchType,
      catalogSearch,
      navigate,
      location.search,
    ]
  );
  // --- Robust utility functions --- //
  const safeNumber = (val, defaultVal) => {
    const num = Number(val);
    return Number.isFinite(num) && num >= 0 ? num : defaultVal;
  };

  const parseCatalog = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === "string") {
      return value
        .split(";")
        .map((c) => c.trim())
        .filter(Boolean);
    }
    return [];
  };

  const hydrateFiltersFromURL = (search) => {
    const query = queryString.parse(search);
    const {
      catalog,
      limit: queryLimit,
      offset: queryOffset,
      ...filters
    } = query;

    const inputKeys = ["folio", "tracking_code"];
    const foundKey = inputKeys.find((key) => filters[key]);
    const inputValue = foundKey ? filters[foundKey] : "";

    // limit debe ser positivo: limit=0 rompería Math.floor(offset / limit)
    // Si la URL no trae limit, usamos la preferencia guardada en localStorage
    const storedLimit = getStoredLimit();
    const parsedLimit = safeNumber(queryLimit, storedLimit);

    return {
      filters,
      catalogArray: parseCatalog(catalog),
      limit: parsedLimit > 0 ? parsedLimit : storedLimit,
      offset: safeNumber(queryOffset, 0),
      inputSearchType: foundKey || "folio",
      inputSearchValue: inputValue,
    };
  };

  // Hidration + Initial Search
  useEffect(() => {
    const {
      filters,
      catalogArray,
      limit,
      offset,
      inputSearchType,
      inputSearchValue,
    } = hydrateFiltersFromURL(location.search);

    // Búsqueda inicial; updateLimit explícito porque el estado `limit` aún no refleja la URL
    hdlSearchReport({
      updateOffset: offset,
      updateLimit: limit,
      filters,
      catalog: catalogArray,
      skipURLUpdate: true,
    });

    // Synchronize states (UI)
    setFilterValues(filters);
    setCatalogSearch(catalogArray);
    setLimit(limit);
    setOffset(offset);
    setInputSearchType(inputSearchType);
    setInputSearchValue(inputSearchValue);

    const restoredPage = Math.floor(offset / limit);
    setCurrentPage(restoredPage);

    // Restaura la ventana de páginas para que incluya la página activa
    const windowIndex = Math.floor(restoredPage / pageNumLimit);
    setMinPageNum(windowIndex * pageNumLimit);
    setMaxPageNum((windowIndex + 1) * pageNumLimit);
  }, [location.search]);
  // Get tags from catalog
  useEffect(() => {
    const fetchData = async () => {
      try {
        const resp = await catalogByPart({
          is_own: false,
          catalogue: "TG-100",
          path: "/",
        });
        if (!resp.error) setTags(resp.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);
  // Open filters menu and set data
  useEffect(() => {
    if (showFilters) {
      setLocalFilterValues(filterValues);
      setLocalCatalogSearch(catalogSearch);
    }
  }, [showFilters]);
  // Save custom filters with name
  const hdlSaveReportFilter = async (
    filters = filterValues,
    catalog = catalogSearch
  ) => {
    const params = {
      ...filters,
      ...(catalog?.some(Boolean) && { catalog: catalog.join(";") }),
    };
    const filter_url = queryString.stringify(params, {
      skipEmptyString: true,
      skipNull: true,
    });

    const payload = {
      filter_url,
      ...(filterName?.trim() && { name: filterName.trim() }),
    };
    const saving = enqueueSnackbar(`${t("ReportsList.saving_filters")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      await saveReportFilters(payload);
      reloadSavedFilters();
      enqueueSnackbar(t("ReportsList.filters_successfully_saved"), {
        variant: "success",
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("ReportsList.filters_save_failed"), {
        variant: "error",
      });
    } finally {
      if (saving) closeSnackbar(saving);
      setFilterName("");
    }
  };
  // Reset pagination
  const resetPagination = () => {
    setCurrentPage(0);
    setMinPageNum(0);
    setMaxPageNum(pageNumLimit);
    setOffset(0);
  };
  // OnClean search input and apply
  const hdlClearSearchInput = () => {
    resetPagination();
    setInputSearchValue("");
    hdlSearchReport({
      updateOffset: 0,
      filters: filterValues,
      catalog: catalogSearch,
      searchValue: "",
      clearInput: true,
    });
  };
  // Apply search with input value
  const hdlSearchFromSearchBar = (searchValue) => {
    hdlSearchReport({ updateOffset: 0, searchValue });
    resetPagination();
  };
  // Apply saved filter
  const hdlApplySavedFilter = (urlOrigin) => {
    const parsed = queryString.parse(urlOrigin);
    const { catalog, ...filters } = parsed;
    const catalogArray = catalog
      ? Array.isArray(catalog)
        ? catalog
        : catalog.split(";")
      : [];
    resetPagination();
    setLocalFilterValues(filters);
    setLocalCatalogSearch(catalogArray);
  };
  // Apply select year created
  const hdlChangeYearCreated = (year) => {
    const newFilters = { ...filterValues, year_created: String(year) };
    setFilterValues(newFilters);
    resetPagination();
    hdlSearchReport({
      updateOffset: 0,
      filters: newFilters,
      catalog: catalogSearch,
    });
  };

  return (
    <>
      {/* Filter menu */}
      <Filters
        open={showFilters}
        onClose={() => setShowFilters(false)}
        localFilterValues={localFilterValues}
        setLocalFilterValues={setLocalFilterValues}
        localCatalogSearch={localCatalogSearch}
        setLocalCatalogSearch={setLocalCatalogSearch}
        onSubmitFilters={(newFilters, newCatalog) => {
          resetPagination();
          setFilterValues(newFilters);
          setCatalogSearch(newCatalog);
          hdlSearchReport({
            updateOffset: 0,
            filters: newFilters,
            catalog: newCatalog,
          });
        }}
        hdlSaveReportFilter={hdlSaveReportFilter}
        filterName={filterName}
        setFilterName={setFilterName}
        status={queryString.parse(location.search).status}
        tags={tags}
        onApplyFilter={hdlApplySavedFilter}
      />
      {/* Titles and route */}
      <EGPageHeader
        breadcrumbItems={[
          { label: t("ReportsList.home"), iconName: "bold_home", route: "/" },
          { label: t("ReportsList.all_reports") },
        ]}
        title={t("ReportsList.all_reports")}
        subtitle={t("ReportsList.all_reports_list")}
        mainIconName="outline_document_text"
        border
      />
      {/* Table titles */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "center", md: "space-between" },
          flexFlow: "row wrap",
          bgcolor: theme.palette.background.paper,
          mt: 2,
          border: `${theme.palette.blueGray100.main} 1px solid`,
          py: { xs: 0.5, sm: 1 },
          px: { xs: 1, sm: 2 },
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        {/* Reports result */}
        <Typography variant="h6" color="secondary" mr={1}>
          {`${t("ReportsList.showing")} ${pagination.count} ${t(
            "ReportsList.report"
          )}${pagination.count === 1 ? "" : "s"}`}
        </Typography>
        {/* Filter search */}
        <SearchBar
          inputSearchValue={inputSearchValue}
          onChangeSearchValue={setInputSearchValue}
          inputSearchType={inputSearchType}
          onChangeSearchType={setInputSearchType}
          onSearch={hdlSearchFromSearchBar}
          onClear={hdlClearSearchInput}
          setShowFilters={setShowFilters}
          yearSelected={Number(filterValues.year_created) || dayjs().year()}
          onChangeYear={hdlChangeYearCreated}
          resetPagination={resetPagination}
        />
      </Box>
      {/* Report Table and pagination */}
      <Box
        sx={{
          mt: -0.3,
          pt: { xs: 1, sm: 1.5 },
          px: { xs: 1, sm: 1.5 },
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
        className="dyTheme1 dyBorder1"
      >
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <Fade in={!isLoading} timeout={1000}>
            <Box>
              <ReportTable
                listReports={listReports}
                expiredReports={expiredReports}
              />
              <Paginator
                count={pagination.count}
                next={pagination.next}
                previous={pagination.previous}
                currentPage={currentPage}
                onReCallReports={(newOffset, newLimit = limit) => {
                  setOffset(newOffset);
                  // Persiste la preferencia solo cuando el usuario cambia el "por página"
                  if (newLimit !== limit && VALID_LIMITS.includes(newLimit)) {
                    localStorage.setItem(
                      REPORTS_PER_PAGE_KEY,
                      String(newLimit)
                    );
                  }
                  hdlSearchReport({
                    updateOffset: newOffset,
                    updateLimit: newLimit,
                  });
                }}
                limit={limit}
                setLimit={setLimit}
                listReports={listReports}
                maxPageNum={maxPageNum}
                minPageNum={minPageNum}
                offset={offset}
                pageNumLimit={pageNumLimit}
                searchPag={searchPag}
                setCurrentPage={setCurrentPage}
                setMaxPageNum={setMaxPageNum}
                setMinPageNum={setMinPageNum}
                setSearchPag={setSearchPag}
              />
            </Box>
          </Fade>
        )}
      </Box>
    </>
  );
};
